import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './login.css';
import bgImage from '../assets/bg1.jpg';
import { API_ENDPOINTS, storeAuthData, isAuthenticated, getUserInfo, isAuthenticatedSync, getUserInfoSync, ACCESS_TOKEN_TTL_MS } from '../config/api';
import { saveOfflineCredential, getOfflineAuthForCredentials } from '../services/offlineAuth';
import offlineApi from '../utils/offlineApi';

const Login = () => {
  const [displayedText, setDisplayedText] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const fullText = 'Decor Masters';
  const typingSpeed = 150; // milliseconds per letter

  // Redirect if already authenticated (check both sync and async)
  useEffect(() => {
    const checkAuth = async () => {
      // Check sync first for immediate redirect
      if (isAuthenticatedSync()) {
        const user = getUserInfoSync();
        if (user) {
          const route = getDashboardRoute(user.role);
          if (user.first_login === true) {
            navigate(`${route}?first_login=true`, { replace: true });
          } else {
            navigate(route, { replace: true });
          }
          return;
        }
      }
      
      // Check async (IndexedDB)
      const authenticated = await isAuthenticated();
      if (authenticated) {
        const user = await getUserInfo();
        if (user) {
          const route = getDashboardRoute(user.role);
          if (user.first_login === true) {
            navigate(`${route}?first_login=true`, { replace: true });
          } else {
            navigate(route, { replace: true });
          }
        }
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Role to dashboard route mapping
  const getDashboardRoute = (role) => {
    const roleMap = {
      'owner': '/owner',
      'admin': '/admin',
      'auditor': '/auditor',
      'branch_manager': '/branch-manager',
      'warehouse_manager': '/warehouse-manager',
      'cashier': '/cashier',
    };
    return roleMap[role.toLowerCase()] || '/';
  };

  useEffect(() => {
    let currentIndex = 0;
    let timeoutId;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex + 1));
        currentIndex++;
        timeoutId = setTimeout(typeText, typingSpeed);
      } else {
        // After typing is complete, wait and restart
        timeoutId = setTimeout(() => {
          setDisplayedText('');
          currentIndex = 0;
          timeoutId = setTimeout(typeText, typingSpeed * 2);
        }, 2000);
      }
    };

    // Start typing after a short delay
    timeoutId = setTimeout(typeText, 500);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check if offline - allow login with cached credentials
      if (!navigator.onLine) {
        const offlineAuth = await getOfflineAuthForCredentials(email, password);
        if (offlineAuth) {
          const accessExpiresAt = offlineAuth.access_expires_at || new Date(Date.now() + ACCESS_TOKEN_TTL_MS).toISOString();
          if (!offlineAuth.refresh_token) {
            console.warn('Offline credentials missing refresh token. User will need to re-authenticate online to sync.');
          }
          await storeAuthData(
            offlineAuth.access_token,
            offlineAuth.refresh_token || '',
            offlineAuth.user_info,
            { accessExpiresAt }
          );
          
          // Verify auth data was stored before navigation
          const verifyAuth = isAuthenticatedSync();
          if (!verifyAuth) {
            await new Promise(resolve => setTimeout(resolve, 100));
            const verifyAuthRetry = isAuthenticatedSync();
            if (!verifyAuthRetry) {
              setError('Failed to store authentication data. Please try again.');
              setLoading(false);
              return;
            }
          }
          
          // Small delay to ensure all auth systems are initialized
          await new Promise(resolve => setTimeout(resolve, 50));
          
          const user = offlineAuth.user_info;
          const route = getDashboardRoute(user.role);
          navigate(route, { replace: true });
          return;
        }
        
        setError('Offline login not available for these credentials. Please connect to the internet and login once to enable offline access.');
        setLoading(false);
        return;
      }
      
      // Online - try API login
      const response = await offlineApi.post(API_ENDPOINTS.LOGIN, {
        email,
        password,
      });
console.log("response", response);
      // Handle queued response (offline)
      if (response.queued) {
        setError('Login queued. You will be logged in when connection is restored.');
        setLoading(false);
        return;
      }

      const { access, refresh, user } = response.data;
      const accessExpiresAt = new Date(Date.now() + ACCESS_TOKEN_TTL_MS).toISOString();

      // Store authentication data
      await storeAuthData(access, refresh, user, { accessExpiresAt });
      await saveOfflineCredential(email, password, {
        access_token: access,
        refresh_token: refresh,
        user_info: user,
        access_expires_at: accessExpiresAt,
      });

      // Fetch and store exchange rate for offline use
      try {
        const { fetchAndStoreExchangeRate } = await import('../utils/exchangeRate');
        await fetchAndStoreExchangeRate();
      } catch (error) {
        console.error('Error fetching exchange rate on login:', error);
        // Don't block login if exchange rate fetch fails
      }

      // Verify auth data was stored before navigation
      // This prevents race conditions on first login
      const verifyAuth = isAuthenticatedSync();
      if (!verifyAuth) {
        // Wait a bit and check again (cookies might need a moment)
        await new Promise(resolve => setTimeout(resolve, 100));
        const verifyAuthRetry = isAuthenticatedSync();
        if (!verifyAuthRetry) {
          setError('Failed to store authentication data. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Small delay to ensure all auth systems are initialized
      await new Promise(resolve => setTimeout(resolve, 50));

      // If first login, redirect to profile page (change password)
      if (user.first_login === true) {
        const route = getDashboardRoute(user.role);
        navigate(`${route}?first_login=true`, { replace: true });
      } else {
        // Navigate based on role
        const route = getDashboardRoute(user.role);
        navigate(route, { replace: true });
      }
    } catch (err) {
      const data = err.response?.data;
      const errorMessage =
        // Prefer explicit non_field_errors array from API (e.g. DRF)
        (Array.isArray(data?.non_field_errors) && data.non_field_errors[0]) ||
        data?.non_field_errors ||
        data?.detail ||
        data?.message ||
        data?.error ||
        // Fallback to generic Axios error message
        err.message ||
        'Login failed. Please check your credentials and try again.';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    const { value: email } = await Swal.fire({
      title: 'Reset Password',
      text: 'Enter your email address and we will send you a password reset link.',
      input: 'email',
      inputPlaceholder: 'Enter your email address',
      inputValidator: (value) => {
        if (!value) {
          return 'You need to enter an email address!';
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address!';
        }
      },
      showCancelButton: true,
      confirmButtonText: 'Send Reset Link',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      showLoaderOnConfirm: true,
      preConfirm: async (email) => {
        try {
          const response = await axios.post(API_ENDPOINTS.RESET_PASSWORD, {
            email: email,
          });
          return response.data;
        } catch (error) {
          const errorMessage = error.response?.data?.message ||
            error.response?.data?.error ||
            'Failed to send reset link. Please try again.';
          try {
            if (Swal && typeof Swal.showValidationMessage === 'function') {
              Swal.showValidationMessage(errorMessage);
            }
          } catch (swalError) {
            // If showValidationMessage fails, we'll still return false to prevent closing
            console.error('Error showing validation message:', swalError);
          }
          return false;
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    });

    if (email) {
      Swal.fire({
        icon: 'success',
        title: 'Reset Link Sent!',
        text: 'Please check your email for password reset instructions.',
        confirmButtonColor: '#3085d6',
      });
    }
  };

  return (
    <section
      className="login-hero"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="login-overlay">
        <div className="login-card">
          <div className="brand">
            <p className="eyebrow">Welcome to</p>
            <h1 className="morph-text">
              {displayedText}
              <span className="cursor">|</span>
            </h1>
            <p className="subtitle">
            Transform your space with Decor Master — your trusted source for premium home décor.
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {error && (
              <div className="error-message" style={{
                color: '#ff4444',
                backgroundColor: '#ffe6e6',
                padding: '10px',
                borderRadius: '4px',
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="you@decomaster.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />

            <label htmlFor="password">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '2.5rem', width: '100%' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.85rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="white"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="white"/>
                  </svg>
                )}
              </button>
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>

            <p className="helper-text">
              Forgot password? <button type="button" onClick={handleResetPassword} style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}>Reset it</button>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Login;